import React, { useState, useEffect, useMemo, useCallback } from "react";
import { useFormContext } from "react-hook-form";
import { FixedSizeList as List } from "react-window";
import { useDebounce } from "use-debounce";

// ✅ Branch from server / dropdown list
interface Branch {
  value: number | string;
  label: string;
  NameAlias?: string;
}

// ✅ What we store in the form
interface SelectedBranch {
  Code: number | string;
  Name: string;
}

interface BranchSelectorProps {
  branches: Branch[];
  name: string;
  label: string;
}

// ✅ Interface for React-Window's itemData
interface ListItemData {
  filteredBranches: Branch[];
  isSelected: (branch: Branch) => boolean;
  handleCheckboxChange: (branch: Branch, checked: boolean) => void;
  checkboxBorderColor: string;
  // Note: we don't need to pass the form name/setValue, as handleCheckboxChange handles it.
}

const BranchSelectorNew: React.FC<BranchSelectorProps> = ({
  branches,
  name,
  label,
}) => {
  const { setValue, watch } = useFormContext();

  // Watch the field value in the form
  const rawSelected = watch(name);
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearchTerm] = useDebounce(searchTerm, 300);

  // ✅ Normalize selections
  const normalizeSelected = (input: any): SelectedBranch[] => {
    if (!input) return [];
    // Already in object form: Check for the expected {Code, Name} structure
    if (Array.isArray(input) && input.every((x) => x && x.Code !== undefined)) {
      return input;
    }
    // Convert from primitive values
    if (Array.isArray(input)) {
      const mapByValue = new Map(branches.map((b) => [String(b.value), b]));
      return input.map((id) => {
        const found = mapByValue.get(String(id));
        return {
          Code: id,
          Name: found ? found.label : String(id),
        };
      });
    }
    return [];
  };

  // Ensure this re-runs when rawSelected changes
  const selectedValues: SelectedBranch[] = useMemo(() => normalizeSelected(rawSelected), [rawSelected, branches]);

  // ✅ Keep form state normalized (runs only if initial value is wrong/primitive)
  useEffect(() => {
    if (!Array.isArray(rawSelected)) {
      setValue(name, [], { shouldDirty: false, shouldTouch: false });
      return;
    }

    // If already normalized, skip
    if (rawSelected.length && rawSelected[0]?.Code !== undefined) return;
    
    // Otherwise normalize to {Code, Name}
    const normalized = normalizeSelected(rawSelected);
    if (normalized.length !== rawSelected.length) { // Prevent infinite loop if already partially normalized
        setValue(name, normalized, { shouldDirty: false, shouldTouch: false });
    }
  }, [rawSelected, branches, name, setValue, normalizeSelected]);

  // ... (Duplicates warning is unchanged)

  // ✅ Handle theme
  const isDarkTheme =
    typeof window !== "undefined" &&
    window.matchMedia &&
    window.matchMedia("(prefers-color-scheme: dark)").matches;
  const checkboxBorderColor = isDarkTheme ? "#ffffff" : "#333333";

  // ✅ Utility to check if a branch is selected (Memoized)
  const isSelected = useCallback(
    (branch: Branch) =>
      selectedValues.some((sv) => String(sv.Code) === String(branch.value)),
    [selectedValues]
  );

  // ✅ Select All handler
  const handleSelectAll = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.checked) {
      // Map to the required {Code, Name} format
      const all = branches.map((b) => ({ Code: b.value, Name: b.label }));
      setValue(name, all, { shouldDirty: true, shouldTouch: true });
    } else {
      setValue(name, [], { shouldDirty: true, shouldTouch: true });
    }
  };

  const isAllSelected =
    branches.length > 0 &&
    selectedValues.length === branches.length &&
    branches.every((branch) => isSelected(branch));

  // ✅ Individual checkbox handler (Memoized)
  const handleCheckboxChange = useCallback(
    (branch: Branch, checked: boolean) => {
      let newValue: SelectedBranch[];
      if (checked) {
        const exists = selectedValues.some(
          (v) => String(v.Code) === String(branch.value)
        );
        newValue = exists
          ? selectedValues
          : [...selectedValues, { Code: branch.value, Name: branch.label }]; // Correct format
      } else {
        newValue = selectedValues.filter(
          (v) => String(v.Code) !== String(branch.value)
        );
      }
      setValue(name, newValue, { shouldDirty: true, shouldTouch: true });
    },
    [selectedValues, name, setValue]
  );

  // ✅ Filter + sort branches
  const filteredBranches = useMemo(() => {
    let filtered = branches;
    if (debouncedSearchTerm) {
      const lowerSearch = debouncedSearchTerm.toLowerCase();
      filtered = branches.filter((branch) =>
        branch.label.toLowerCase().includes(lowerSearch)
      );
    }

    // Move selected to top (depends on isSelected, which depends on selectedValues)
    return [...filtered].sort((a, b) => {
      const aChecked = isSelected(a);
      const bChecked = isSelected(b);
      if (aChecked && !bChecked) return -1;
      if (!aChecked && bChecked) return 1;
      return 0;
    });
  }, [branches, debouncedSearchTerm, isSelected]);


  // ✅ Item Data for React-Window (Crucial fix for re-rendering)
  const itemData: ListItemData = useMemo(() => ({
    filteredBranches,
    isSelected,
    handleCheckboxChange,
    checkboxBorderColor,
  }), [filteredBranches, isSelected, handleCheckboxChange, checkboxBorderColor]);


  // ✅ React-window Row
  const Row = ({
    index,
    style,
    data, // ⬅️ Must receive data prop
  }: {
    index: number;
    style: React.CSSProperties;
    data: ListItemData;
  }) => {
    const { filteredBranches, isSelected, handleCheckboxChange, checkboxBorderColor } = data;
    
    const branch = filteredBranches[index];
    if (!branch) return null;
    
    // Get checked status from isSelected passed via data
    const checked = isSelected(branch); 

    return (
      <div style={style} className="form-check mb-2 d-flex align-items-center">
        <input
          type="checkbox"
          id={`branch-${name}-${branch.value}`}
          value={String(branch.value)}
          checked={checked} // ⬅️ Now uses the latest state
          onChange={(e) => handleCheckboxChange(branch, e.target.checked)}
          className="form-check-input h-5 w-5 text-blue-600 rounded focus:ring-blue-500 cursor-pointer"
          style={{
            border: `2px solid ${checkboxBorderColor}`,
            borderRadius: "4px",
            cursor: "pointer",
          }}
        />
        <label
          htmlFor={`branch-${name}-${branch.value}`}
          className="form-check-label ms-2 text-gray-700"
        >
          {branch.label}
        </label>
      </div>
    );
  };

  return (
    <div className="mt-1 relative mb-2">
      <div className="pt-2">
        {/* ✅ Select All */}
        <div className="form-check mb-1">
          <input
            type="checkbox"
            id={`selectAll-${name}`}
            className="form-check-input h-5 w-5 text-blue-600 rounded focus:ring-blue-500 cursor-pointer"
            checked={isAllSelected}
            onChange={handleSelectAll}
            style={{
              border: `2px solid ${checkboxBorderColor}`,
              borderRadius: "4px",
              cursor: "pointer",
            }}
          />
          <label
            htmlFor={`selectAll-${name}`}
            className="form-check-label fw-bold ms-2 text-gray-800"
          >
            All {label} ({branches.length})
          </label>
        </div>

        {/* ✅ Search */}
        <div className="mb-1">
          <input
            type="text"
            placeholder={`Search ${label}...`}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="form-control"
          />
        </div>

        {/* ✅ Branch list */}
        <div
          className="border rounded-lg p-3 shadow-sm"
          style={{ height: "16rem", overflow: "hidden" }}
        >
          {filteredBranches.length > 0 ? (
            <List
              height={237}
              itemCount={filteredBranches.length}
              itemSize={40}
              width="100%"
              itemData={itemData} // ⬅️ CRITICAL FIX: Pass dynamic data
            >
              {Row}
            </List>
          ) : (
            <p className="text-gray-500 text-center">No {label} found</p>
          )}
        </div>
      </div>
    </div>
  );
};

export default BranchSelectorNew;